import ErrorIcon from "@mui/icons-material/Error";
import { Button, Link, Paper, Tooltip } from "@mui/material";
import { Form, Formik, useFormikContext } from "formik";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import InputField from "../../common/FormFields/InputField";
import { usePutCurrentUserAttributesMutation } from "../../redux/api/user";
import {
  refresh,
  selectCurrentProfile,
  selectCurrentUser,
} from "../../redux/authentication/authSlice";
import { usePrompt } from "../../util/usePrompt";
import formData from "./formValidation/formData";
import initialValues from "./formValidation/initialValues";
import validationSchema from "./formValidation/validationSchema";
import "./CognitoUserForm.scss";
import { formatLocaleDateStringToIso } from "../../common/FormatDateString";
import { MARINER_SUPPORT_EMAIL } from "../../common/MarinerSupportEmail";

const CognitoUserForm = () => {
  const user = useSelector(selectCurrentUser);
  const profile = useSelector(selectCurrentProfile);
  const dispatch = useDispatch();
  const [updateUserAttributes] = usePutCurrentUserAttributesMutation();
  const { formId, formField } = formData;
  const { name, dateOfBirth } = formField;

  let getInitialValues = () => {
    if (!!user) {
      return {
        [name.name]: user?.name,
        [dateOfBirth.name]: formatLocaleDateStringToIso(user?.birthdate),
      };
    } else {
      return initialValues;
    }
  };

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(MARINER_SUPPORT_EMAIL);
    toast.success(<p>Copied to clipboard successfully.</p>, {
      position: "top-center",
      autoClose: 5000,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "colored",
    });
  };

  const _handleSubmit = async (values: any, actions: any) => {
    if (!!user) {
      if (!values?.name.trim() || !values?.dateOfBirth) {
        actions.setSubmitting(false);
        toast.error(<p>Name and Date of Birth are required.</p>, {
          position: "top-center",
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "colored",
        });
        return;
      }
      const updateResult = await updateUserAttributes({
        attributes: {
          name: values.name.trim(),
          dateOfBirth: values.dateOfBirth,
        },
      });
      if ("error" in updateResult) {
        actions.setSubmitting(false);
        return;
      }

      actions.setSubmitting(false);
      toast.success(<p>User updated successfully.</p>, {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });

      dispatch(refresh(null));
    }
  };

  const IsFormDirty = () => {
    const { dirty } = useFormikContext();
    usePrompt("Changes will not be saved, would you like to leave?", dirty);
    return <></>;
  };

  return (
    <div>
      {profile === null && (
        <div className="error-message-box">
          <ErrorIcon className="color-red mt-1" />
          <div className="error-message flex-col">
            <div className="color-red">
              <p className="mb-1">
                *One or more of your responses do not match, please verify your
                information and try again.
              </p>
              <p className="mb-1">
                **Email Address, Name, and Birthday must match those on file
                with TOTE Services.
              </p>
            </div>
            <span className="mt-1 email-help-text">
              If you need assistance please contact
              <Tooltip title="Copy Address to Clipboard" placement="top">
                <Link
                  className="blue-link"
                  onClick={() => handleCopyToClipboard()}
                >
                  {MARINER_SUPPORT_EMAIL}
                </Link>
              </Tooltip>
            </span>
          </div>
        </div>
      )}
      <Formik
        enableReinitialize={true}
        initialValues={getInitialValues()}
        validationSchema={validationSchema}
        onSubmit={_handleSubmit}
      >
        {({ isSubmitting, values, setFieldValue, errors }) => (
          <Form id={formId}>
            <IsFormDirty />
            <div className="user-details">
              <div className="user-header my-2">
                <h1>User Details</h1>
                <Button
                  variant="contained"
                  className="blue-button-small"
                  type="submit"
                  form="cognitoUserForm"
                  disabled={isSubmitting}
                >
                  Save Changes
                </Button>
              </div>
              <Paper>
                <div className="user-input">
                  <label>
                    <span>Email:</span>
                    <input disabled={true} value={user?.email} />
                  </label>
                  <div className="user-row-2">
                    <label>
                      <span className="required">{name.label}</span>
                      <InputField
                        name={name.name}
                        fullWidth
                        size="small"
                        type="text"
                      />
                    </label>
                    <label className="user-date">
                      <span className="required">{dateOfBirth.label}</span>
                      <InputField
                        name={dateOfBirth.name}
                        fullWidth
                        size="small"
                        type="date"
                      />
                    </label>
                  </div>
                </div>
              </Paper>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default CognitoUserForm;
